// ============================================================================
// DESIGNATION HOOKS
// ============================================================================
// React hooks for the Designation master.
//
// Pattern follows useEngagementTypes.ts:
// - useDesignations(params)            → list with pagination, refetch
// - useDesignation(id)                 → single record by id
// - useCreateDesignation()             → create mutation
// - useUpdateDesignation()             → update mutation
// - useDeleteDesignation()             → delete mutation
//
// Note: useDesignations serializes params via JSON.stringify in the dep
// array to avoid infinite re-render when callers pass an inline object.

import { useState, useEffect, useCallback, useMemo } from 'react';
import { designationService } from 'api/services/designationService';
import type {
  ID,
  PaginatedResponse,
  Designation,
  CreateDesignationRequest,
  UpdateDesignationRequest,
  DesignationSearchRequest,
} from 'api/types';

const EMPTY_PAGINATION = {
  currentPage: 1,
  pageSize: 25,
  totalPages: 0,
  totalItems: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

/**
 * Hook to fetch a paginated list of designations.
 * Refetches automatically when `params` changes.
 */
export const useDesignations = (params?: DesignationSearchRequest) => {
  const [data, setData] = useState<Designation[]>([]);
  const [pagination, setPagination] = useState<PaginatedResponse<Designation>['pagination']>(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);

  const fetchDesignations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await designationService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchDesignations();
  }, [fetchDesignations]);

  return {
    data,
    pagination,
    loading,
    error,
    refetch: fetchDesignations,
  };
};

/**
 * Hook to fetch a single designation by id.
 */
export const useDesignation = (id: ID | null | undefined) => {
  const [data, setData] = useState<Designation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchDesignation = useCallback(async () => {
    if (!id) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await designationService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDesignation();
  }, [fetchDesignation]);

  return { data, loading, error, refetch: fetchDesignation };
};

/**
 * Hook to create a new designation.
 */
export const useCreateDesignation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const createDesignation = async (
    data: CreateDesignationRequest,
    options?: {
      onSuccess?: (item: Designation) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await designationService.create(data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { createDesignation, loading, error, success };
};

/**
 * Hook to update a designation. Always pass the latest rowVersion.
 */
export const useUpdateDesignation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const updateDesignation = async (
    id: ID,
    data: UpdateDesignationRequest,
    options?: {
      onSuccess?: (item: Designation) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await designationService.update(id, data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { updateDesignation, loading, error, success };
};

/**
 * Hook to delete a designation (soft-delete on the server).
 */
export const useDeleteDesignation = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const deleteDesignation = async (
    id: ID,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await designationService.delete(id);
      setSuccess(true);
      options?.onSuccess?.();
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { deleteDesignation, loading, error, success };
};
